// THE gene-ring derivation (A4). Sunburst, ZoomableSunburst and WheelInspector all build the ring
// here — three copies of `new Set(snp.gene)` used to disagree by omission, and `gene-{name}` node
// ids collided whenever two traits shared a symbol.
import { WheelGeneRef } from '../../types/geneCodes/Panel';

// Structural on purpose: the wheels pass loosely-typed rows, the dashboard passes Staging[].
export interface RingSnp {
	gene?: string | null;
	subcategory?: string | null;
	wheel_gene_id?: number | null;
}

export interface RingTrait {
	trait_name: string;
	genes?: WheelGeneRef[];
}

export interface GeneRingEntry<S extends RingSnp> {
	/** `gene-{id}` when the gene has a server row — distinct across traits by construction — else the legacy `gene-{name}`. */
	nodeId: string;
	name: string;
	wheelGeneId: number | null;
	registryRef: string | null;
	/** The server's explicit ring position (A5 reorder); null = unordered, sorts after ordered rows. */
	sortOrder: number | null;
	snps: S[];
}

const serverGeneFor = (genes: WheelGeneRef[] | undefined, linkedId: number | null, name: string): WheelGeneRef | undefined => {
	if (!genes) return undefined;
	// The SNP's own row link is the most direct identity; the symbol match covers SNPs the
	// endpoint served before their link existed. Comparison is case-insensitive like the server's.
	if (linkedId != null) {
		const byId = genes.find((gene) => gene.id === linkedId);
		if (byId) return byId;
	}
	return genes.find((gene) => gene.id != null && gene.symbol.toLowerCase() === name.toLowerCase());
};

/**
 * The gene ring for one trait. Membership and order come from the SNP rows exactly as the legacy
 * derivations computed them (first appearance wins), so a panel with no server genes renders the
 * identical name-keyed ring; the server list only supplies each gene's stable identity.
 *
 * R6: a gene is a record now, so the ring is the UNION — a server row no SNP names is appended with
 * an empty `snps`. `includeSnpless` is false only where the SNPs are filtered to a patient's own
 * results: an unmatched row there means "no data for this gene", not "an authored node".
 */
export const geneRingForTrait = <S extends RingSnp>(trait: RingTrait, snps: S[], includeSnpless: boolean = true): GeneRingEntry<S>[] => {
	const order: string[] = [];
	const grouped = new Map<string, S[]>();

	snps.forEach((snp) => {
		if (!snp.gene || snp.subcategory !== trait.trait_name) return;
		if (!grouped.has(snp.gene)) {
			grouped.set(snp.gene, []);
			order.push(snp.gene);
		}
		grouped.get(snp.gene)!.push(snp);
	});

	const entries = order.map((name) => {
		const geneSnps = grouped.get(name)!;
		const linkedId = geneSnps.find((snp) => snp.wheel_gene_id != null)?.wheel_gene_id ?? null;
		const server = serverGeneFor(trait.genes, linkedId, name);
		const wheelGeneId = linkedId ?? server?.id ?? null;

		return {
			nodeId: wheelGeneId != null ? `gene-${wheelGeneId}` : `gene-${name}`,
			name,
			wheelGeneId,
			registryRef: server?.registry_ref ?? null,
			sortOrder: server?.sort_order ?? null,
			snps: geneSnps,
		};
	});

	if (includeSnpless) {
		// Both keys, or a row resolved by id under a different spelling would be appended twice.
		const coveredNames = new Set(entries.map((entry) => entry.name.toLowerCase()));
		const coveredIds = new Set(entries.map((entry) => entry.wheelGeneId).filter((id): id is number => id != null));

		(trait.genes ?? []).forEach((gene) => {
			if (gene.id == null || coveredIds.has(gene.id) || coveredNames.has(gene.symbol.toLowerCase())) return;
			coveredIds.add(gene.id);
			coveredNames.add(gene.symbol.toLowerCase());
			entries.push({
				nodeId: `gene-${gene.id}`,
				name: gene.symbol,
				wheelGeneId: gene.id,
				registryRef: gene.registry_ref ?? null,
				sortOrder: gene.sort_order ?? null,
				snps: [],
			});
		});
	}

	// A5: an explicit sort_order wins; NULLs keep first-appearance order AFTER the ordered rows —
	// the client half of the server's `sort_order IS NULL, sort_order, id`. All-null (every ring
	// before the first reorder) is byte-identical to the legacy first-appearance ring.
	return entries
		.map((entry, index) => ({ entry, index }))
		.sort((left, right) => {
			if (left.entry.sortOrder == null && right.entry.sortOrder == null) return left.index - right.index;
			if (left.entry.sortOrder == null) return 1;
			if (right.entry.sortOrder == null) return -1;
			return left.entry.sortOrder === right.entry.sortOrder ? left.index - right.index : left.entry.sortOrder - right.entry.sortOrder;
		})
		.map(({ entry }) => entry);
};

/** The server id for a selected gene name, for the Xoe focus publication — null when it has no row yet. */
export const resolveWheelGeneId = (trait: RingTrait | null, snps: RingSnp[], geneName: string | null): number | null => {
	if (!trait || !geneName) return null;
	return geneRingForTrait(trait, snps).find((entry) => entry.name === geneName)?.wheelGeneId ?? null;
};
